import React, { Component } from 'react';
import axios from "axios";
import { Page } from "../../../model/page";
import "./template_component.scss";
import { toQueryString } from "../../../utils/utils";
import { Translation } from "react-i18next";
import { TFunction } from "i18next";
import { extraSmallLayout } from "../../../utils/device";
import { API_CONFIG } from '../../../config/api_config';

export interface TemplateComponentProps<D = any> {
    url?: string;
    id?: string;

    data?: D;

    layoutColumns: number;
    language: string;
    query?: any;
    path?: string;

    navigate?: any;
    course?: any;
}

export abstract class TemplateComponent<D = any, P extends TemplateComponentProps<D> = TemplateComponentProps<D>> extends Component<P, any> {

    state: {
        data?: Page,
        isFetching: boolean
    } | any;

    dataUrl?: string;
    mounted: boolean = false;

    // if it is set to true, we will call the internationComponentRender() with the translation function "t"
    requireInternationalization: boolean = false;
    forceReload: boolean = false;


    constructor(props: P) {
        super(props);

        this.state = {
            isFetching: false
        };

        if (this.props.url) {
            this.dataUrl = this.props.url;
        }
    }

    onDataLoaded(data: D) { }

    onDataLoadingFailed() { }

    componentDidMount(): void {
        this.mounted = true;

        // if the data has already been passed from a parent component, we don't try to fetch it
        if (((!this.props.data && !this.state.data) || this.forceReload) && this.dataUrl) {
            this.fetchData(this.dataUrl).then(data => data ? this.onDataLoaded(data) : this.onDataLoadingFailed());
        }
    }


    componentDidUpdate(prevProps: Readonly<P>, prevState: Readonly<any>, snapshot?: any): void {

        if (this.props.url && prevProps.url !== this.props.url) {

            // if the data has already been passed from a parent component, we don't try to fetch it

            this.fetchData(this.props.url!).then((data: any) => data ? this.onDataLoaded(data) : null);
        }
    }


    componentWillUnmount(): void {
        this.mounted = false;
    }

    updateQueryString(params: any) {
        const baseUrl = ((this.props.path ? this.props.path : "") as string).split("?")[0];
        window.history.pushState({}, window.document.title, `${baseUrl}?${toQueryString(params)}`);
    }

    prefilterData(data: any): D {
        return data;
    }

    /**
     * The method asynchronously fetch the data from the url specified.
     * Before the fetching starts we set the state variable "isFetching" to true,
     * to signal that the fetching has started, which is going to be reset to false when the fetching has completed.
     *
     * @param url - the url where to fetch the data
     */
    async fetchData(url: string) {
        try {
            this.setState({ isFetching: true });
            console.log("Loading contents from " + url);
            let data: any = {}
            let response: any = {}
            response = await axios.get(url);
            data = response.data;
            console.log(data);


            let stateUpdates: any = { isFetching: false };
            if (response.status < 300) {
                console.log("Content from url " + url + " has been loaded");
                data = this.prefilterData(data);
                stateUpdates["data"] = data;
            } else {
                console.log("Cannot load data for at url " + url);
                console.log(response);
            }
            this.setState(stateUpdates)

            return data;

        } catch (e) {

            console.warn("WARNING: Data retrieve failed for url " + url, e);

            if (this.mounted) {
                this.setState({ isFetching: false });
            }

            return null;
        }
    }

    /**
     * In the render method the component returned by componentRender is wrapped inside a div,
     * which has the CSS classes to identify if we are on mobile or desktop and if we are showing a one or two columns layout
     */
    render(): React.ReactElement | string | number | {} | React.ReactNodeArray | React.ReactPortal | boolean | null | undefined {

        const data: any | undefined = this.state.data || this.props.data;

        if (!data || data.hidden) return <div />;

        let parsedData: D;

        try {
            if (data instanceof String) {
                parsedData = JSON.parse(data.trim());
            } else {
                parsedData = data;
            }

        } catch (e) {
            console.warn("WARNING: Cannot parse data returned at url " + this.dataUrl, e);
            return <div />
        }

        let idProps = {};

        if (this.props.id) {
            idProps = {
                id: this.props.id
            };
        }

        try {
            return (
                <div className={`template-component ${extraSmallLayout() ? "mobile" : "desktop"} page-layout-${this.props.layoutColumns}-col`}>
                    {   /* If the component has an id we add an anchor item with the id,
                     * so that the internal links will be able to scroll to the component.
                     * The anchor has not exactly the same position of the component but it's a bit upper,
                     * so that when the page scrolls to the components its title will not be hidden by the navigation bar
                     */
                        this.props.id &&
                        <div className="component-anchor" {...idProps} />
                    }
                    {
                        this.requireInternationalization ?
                            <Translation key="component-translation">
                                {(t: TFunction) => this.internationalComponentRender(parsedData, t)}
                            </Translation>
                            :
                            this.componentRender(parsedData)
                    }
                </div>
            );

        } catch (e) {
            console.error("ERROR: Uncaught exception in component " + this.constructor.name, e);
            return <div />;

        }
    }

    componentRender(data: D): React.ReactElement {
        return <div />;
    }

    internationalComponentRender(data: D, t: TFunction): React.ReactElement {
        return this.componentRender(data);
    }
}

export default TemplateComponent;
